import AddIcon from '@mui/icons-material/Add';
import { RequiredPermission } from 'shared/permissions';
import { EnhancedButtonProps, EnhancedButton } from './EnhancedButton';

/**
 * Proprietà specifiche per il componente `AddEntityButton`.
 * Estende `EnhancedButtonProps` rendendo la prop `label` opzionale e mantenendo
 * il supporto retrocompatibile per le proprietà legacy.
 */
export interface AddEnhancedButtonProps extends Partial<Omit<EnhancedButtonProps, 'label'>> {
  /**
   * La parte finale della chiave di traduzione (es: "add_category").
   * @default "add"
   */
  label?: string;

  /**
   * MANTENUTO PER RETROCOMPATIBILITÀ.
   * Se impostato a true, richiede esplicitamente il ruolo TenantAdmin.
   * @default false
   */
  requireTenantAdmin?: boolean;
}

/**
 * `AddEntityButton` è una specializzazione di `EnhancedButton` focalizzata sulle azioni di creazione/aggiunta.
 *
 * Preimposta lo stile nativo `contained` e l'icona `AddIcon`. Gestisce in modo trasparente
 * sia il comportamento di navigazione (se viene passata la prop `to`) sia il comportamento
 * ad azione tramite callback (se viene passata la prop `onClick`).
 *
 * @component
 * @example
 * ```tsx
 * Utilizzo legacy retrocompatibile (Navigazione + Permesso vecchio)
 * <AddEntityButton prefix="categories" to="new" requireTenantAdmin={true} />
 *
 * Utilizzo moderno (Azione Modale + Permessi multipli avanzati)
 * <AddEntityButton
 *   label="add_user"
 *   onClick={handleOpenModal}
 *   requirePermission={["requireAuth", "superAdmin"]}
 * />
 * ```
 */
export default function AddEnhancedButton({
  label = 'add',
  variant = 'contained',
  size = 'small',
  icon = AddIcon,
  requireTenantAdmin = false,
  requirePermission,
  ...props
}: AddEnhancedButtonProps) {
  const resolvedPermissions: RequiredPermission[] = [];

  if (requireTenantAdmin) {
    resolvedPermissions.push('tenantAdmin');
  }

  if (requirePermission) {
    if (Array.isArray(requirePermission)) {
      resolvedPermissions.push(...requirePermission);
    } else {
      resolvedPermissions.push(requirePermission);
    }
  }

  return (
    <EnhancedButton
      label={label}
      variant={variant}
      size={size}
      icon={icon}
      requirePermission={resolvedPermissions.length > 0 ? resolvedPermissions : undefined}
      {...props}
    />
  );
}
